import { Component, ChangeDetectionStrategy } from "@angular/core";
import { ToastService } from "src/app/services/toast.service";
import { TenantService } from "src/app/services/tenant.service";
import { MatDialog } from "@angular/material/dialog";
import { Tenant } from "src/app/models/tenant.model";
import nameOf from "src/app/shared/form/nameOf.helper";
import {
    ConfirmDialogComponent,
    ConfirmDialogData,
} from "src/app/shared/confirmDialog/confirmDialog.component";
import { AuthService } from "src/app/services";
import { TimeZoneService } from "src/app/services/timeZone.service";
import { BaseGridComponent } from "src/app/shared/grid/baseGrid.component";
import { TenantUsersDialogComponent } from "src/app/modals/tenantUsersDialog/tenantUsersDialog.component";
import { TenantDialogComponent } from "src/app/modals/tenant-dialog/tenant-dialog.component";
import { UploadLogoDialogComponent } from "src/app/modals/uploadLogoDialog/uploadLogoDialog.component";
import { CommonService } from "src/app/services/common.service";
import { GridViewService } from "src/app/services/grid-view.service";
import { TranslateService } from "@ngx-translate/core";

@Component({
  standalone: false,
    selector: "app-tenant-list",
    templateUrl: "./tenantList.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ["./tenantList.component.scss"],
})
export class TenantListComponent extends BaseGridComponent<Tenant> {
    timeZoneLabel: string;

    constructor(
        protected tenantService: TenantService,
        toastService: ToastService,
        authService: AuthService,
        timeZoneService: TimeZoneService,
        dialog: MatDialog,
        commonService: CommonService,
        gridViewService: GridViewService,
        private translateService: TranslateService,
    ) {
        super(toastService, dialog, commonService, gridViewService);
        this.timeZoneLabel = timeZoneService.createTimeZoneLabel(
            authService.getCurrentUser().userTimeZone,
        );
        this.gridService = tenantService;
        this.displayedColumns = [
            nameOf<Tenant>((x) => x.name),
            nameOf<Tenant>((x) => x.status),
            nameOf<Tenant>((x) => x.isDistributed),
            nameOf<Tenant>((x) => x.dateCreated),
            nameOf<Tenant>((x) => x.dateUpdated),
            "action",
        ];
    }

    addTenant() {
        const dialogRef = this.dialog.open(TenantDialogComponent, {
            data: {},
            width: "760px",
            disableClose: true,
        });
        this.handleDialogClose(dialogRef);
    }

    editTenant(tenant: Tenant) {
        const dialogRef = this.dialog.open(TenantDialogComponent, {
            data: { id: tenant.id },
            width: "760px",
            disableClose: true,
        });
        this.handleDialogClose(dialogRef);
    }

    showUsers(tenant: Tenant) {
        const dialogRef = this.dialog.open(TenantUsersDialogComponent, {
            data: { tenantId: tenant.id, tenantName: tenant.name },
            width: "450px",
            disableClose: true,
        });
        this.handleDialogClose(dialogRef);
    }

    editLogo(tenant: Tenant) {
        const dialogRef = this.dialog.open(UploadLogoDialogComponent, {
            data: { id: tenant.id },
            width: "450px",
            disableClose: true,
        });
        this.handleDialogClose(dialogRef);
    }

    changeStatus(tenant: Tenant, status: boolean) {
        this.isLoading = true;
        this.tenantService.changeStatus(tenant.id, status).subscribe(
            (response) => {
                this.isLoading = false;
                this.loadPage();
            },
            (error) => {
                this.isLoading = false;
                this.toastService.displayErrorMessage(
                    this.translateService.instant("Error changing tenant status"),
                    true,
                );
            },
        );
    }

    delete(tenant: Tenant) {
        const data: ConfirmDialogData = {
            title: this.translateService.instant("Delete Tenant"),
            message: this.translateService.instant(
                "Are you sure you want to delete tenant {{name}}?",
                { name: tenant.name },
            ),
            primaryButtonText: this.translateService.instant("Delete"),
            primaryCallback: () => this.tenantService.deleteTenant(tenant.id),
            successMessage: this.translateService.instant(
                "Tenant deleted successfully!",
            ),
            secondaryButtonText: this.translateService.instant("Cancel"),
            errorMessage: this.translateService.instant("Not implemented!"),
        };
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data,
            width: "450px",
            disableClose: true,
        });
        this.handleDialogClose(dialogRef);
    }
}
